import { useMemo } from 'react'
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'

interface DraftInfo {
  conversationUrn: string
  text: string
}

export const draftsKey = (userId: string | undefined): QueryKey => ['drafts', userId ?? 'anon']

export function useDrafts() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = draftsKey(user?.id)

  const query = useQuery<DraftInfo[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('drafts')
        .select('conversation_urn, text')
        .neq('text', '')
      if (error) throw new Error(error.message)
      return (data ?? []).map((d) => ({ conversationUrn: d.conversation_urn, text: d.text }))
    },
  })

  // Realtime invalidation is handled centrally by <RealtimeBridge />.

  // Memoise the array itself — `query.data ?? []` returns a fresh `[]` on
  // every render while data is undefined, which would defeat `draftUrns`'
  // memoisation and cascade into every consumer that takes `drafts` as a dep.
  const drafts = useMemo(() => query.data ?? [], [query.data])
  const draftUrns = useMemo(() => new Set(drafts.map((d) => d.conversationUrn)), [drafts])

  return {
    drafts,
    draftUrns,
    draftCount: drafts.length,
    loading: !!user && query.isPending,
    getDraftText: (conversationUrn: string): string | null => {
      const d = drafts.find((d) => d.conversationUrn === conversationUrn)
      return d?.text ?? null
    },
    // Immediate local removal — clears the draft from the visible list before
    // the Supabase realtime tick arrives. The useDraft hook (singular) that
    // owns writes invokes this after deleting the row.
    removeDraftLocally: (conversationUrn: string) => {
      qc.setQueryData<DraftInfo[]>(key, (prev) =>
        (prev ?? []).filter((d) => d.conversationUrn !== conversationUrn),
      )
    },
    refresh: () => qc.invalidateQueries({ queryKey: key }),
  }
}
